import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, Sparkles, ImageIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../ui/dialog';
import { Button } from '../../../ui/button';
import { useApi } from '../../../../hooks/useApi';
import { PromptBox, PROMPT_LIMIT } from './PromptBox';
import { AiPreviewCanvas, ErrorBox, IllustrationInfo, PaidPlan } from './aiGenerateParts';
import { illustrationLayer, looksLikeScene, withLayer } from './illustrationLayer';

const LAYOUTS = { iceberg: 'Iceberg layout', roadmap: 'Roadmap layout', flywheel: 'Flywheel layout' };
const TEXT = {
  auto: { title: 'Generate with AI', icon: Sparkles, desc: 'Describe a diagram, an illustration, or both. You see every paid AI call and a preview before anything changes.' },
  illustration: { title: 'AI Illustration', icon: ImageIcon, desc: 'Describe a picture. It is added as a movable, resizable, rotatable image layer; your board content stays.' },
};

// Phase 3.7A. Flow: prompt -> FREE plan (which paid calls are needed) -> diagram-only runs as before; anything with an
// illustration shows the paid-call confirmation -> preview (diagram + separate picture layer) -> accept / add / cancel.
export function AiWhiteboardDialog({ open, onOpenChange, hasObjects, background, onAccept, onAddLayer, mode = 'auto' }) {
  const { id: deckId } = useParams();
  const { post, del } = useApi();
  const [prompt, setPrompt] = useState('');
  const [status, setStatus] = useState('idle'); // idle | planning | confirm | loading | preview | error
  const [plan, setPlan] = useState(null);
  const [choice, setChoice] = useState(null);
  const [diagram, setDiagram] = useState(null);
  const [ill, setIll] = useState(null);
  const [errors, setErrors] = useState({});
  const [variant, setVariant] = useState('layer');
  const busy = status === 'planning' || status === 'loading';
  const t = TEXT[mode];

  const discard = () => { if (ill?.id) del(`/whiteboard-ai/illustration/${ill.id}`).catch(() => {}); setIll(null); };
  const resetResults = () => { discard(); setDiagram(null); setErrors({}); setVariant('layer'); };
  const close = () => { if (busy) return; resetResults(); onOpenChange(false); setStatus('idle'); setPlan(null); };

  const run = async (c) => {
    setStatus('loading'); setErrors({}); setDiagram(null); setIll(null); setVariant('layer');
    const jobs = [
      c.diagram ? post('/whiteboard-ai/generate', { deck_id: deckId, prompt: prompt.trim() }) : null,
      c.illustration ? post('/whiteboard-ai/illustration', { deck_id: deckId, subject: c.subject.trim(), placement: c.placement, transparent: c.transparent, confirm_paid: true }) : null,
    ];
    const [d, i] = await Promise.allSettled(jobs.map((j) => j || Promise.resolve(null)));
    const errs = {
      diagram: d.status === 'rejected' ? (d.reason?.message || 'AI diagram failed.') : '',
      illustration: i.status === 'rejected' ? (i.reason?.message || 'AI illustration failed.') : '',
    };
    const dv = d.status === 'fulfilled' ? d.value : null, iv = i.status === 'fulfilled' ? i.value : null;
    setDiagram(dv); setIll(iv); setErrors(errs);
    if (!dv?.objects?.length && !iv) { setStatus('error'); return; }
    setStatus('preview');
  };

  const start = async () => {
    if (prompt.trim().length < 3 || prompt.length > PROMPT_LIMIT || busy) return;
    if (mode === 'illustration') {
      const s = prompt.trim().replace(/^(?:please\s+)?(?:(?:create|generate|draw|make|show|add|give me|i want)\s+)+/i, '').replace(/[.\s]+$/, '');
      setPlan({ needs_diagram: false, illustration: true });
      setChoice({ diagram: false, illustration: true, subject: s.slice(0, 1000), placement: 'background', transparent: !looksLikeScene(s) });
      setStatus('confirm');
      return;
    }
    setStatus('planning'); setErrors({});
    try {
      const p = await post('/whiteboard-ai/plan', { deck_id: deckId, prompt: prompt.trim() });
      const f = p.illustrations?.[0];
      const c = { diagram: p.needs_diagram, illustration: !!f, subject: f?.subject || '', placement: f?.placement || 'background', transparent: f?.transparent ?? true };
      setPlan({ needs_diagram: p.needs_diagram, illustration: !!f });
      setChoice(c);
      if (!f) run(c); // diagram only: same single text call as before
      else setStatus('confirm');
    } catch (e) { setErrors({ diagram: e.message || 'Could not read the request.' }); setStatus('error'); }
  };

  const layerFor = async () => {
    const { url } = await post(`/whiteboard-ai/illustration/${ill.id}/accept`, { variant });
    return illustrationLayer({ ...ill, url });
  };
  const finish = () => { setIll(null); setDiagram(null); setStatus('idle'); setPlan(null); setPrompt(''); onOpenChange(false); };
  const replaceAll = async () => {
    if (hasObjects && !window.confirm('Replace everything currently on this whiteboard with the AI proposal?')) return;
    try { onAccept(withLayer(diagram.objects, ill ? await layerFor() : null)); finish(); } catch (e) { toast.error(e.message || 'Could not add the illustration.'); }
  };
  const addIllustration = async () => {
    try { onAddLayer(await layerFor()); finish(); } catch (e) { toast.error(e.message || 'Could not add the illustration.'); }
  };

  const previewUrl = ill && (variant === 'original' && ill.original_url ? ill.original_url : ill.url);
  const previewObjects = withLayer(diagram?.objects || [], ill ? illustrationLayer({ ...ill, url: previewUrl }) : null);
  const nPaid = choice ? (choice.diagram ? 1 : 0) + (choice.illustration ? 1 : 0) : 0;
  const Icon = t.icon;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) close(); }}>
      <DialogContent className="w-[calc(100vw-1.5rem)] max-w-[calc(100vw-1.5rem)] rounded-lg sm:max-w-[760px] max-h-[92vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&>*]:min-w-0" data-testid="wb-ai-dialog" data-mode={mode}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Icon className="h-4 w-4" />{t.title}</DialogTitle>
          <DialogDescription>{t.desc}</DialogDescription>
        </DialogHeader>

        {['idle', 'planning', 'error'].includes(status) ? (
          <div className="space-y-3">
            <PromptBox testid="wb-ai-prompt" value={prompt} disabled={busy} onChange={setPrompt}
              placeholder={mode === 'illustration' ? 'e.g. A beautiful translucent iceberg, soft blue light, calm water.'
                : 'Describe it in as much detail as you like, e.g. A conflict engagement diagram with editable labels and arrows, and a beautiful translucent iceberg behind the content.'} />
            <p className="text-xs text-muted-foreground" data-testid="wb-ai-credit-note">
              Diagrams use one text AI call; illustrations use one image AI call, which you confirm first. Nothing on your board changes until you accept.
            </p>
            <ErrorBox error={status === 'error' ? (errors.diagram || errors.illustration || 'AI generation failed. Nothing was changed.') : ''} />
            <ErrorBox testid="wb-ai-ill-error" error={status === 'error' && errors.diagram && errors.illustration ? errors.illustration : ''} />
          </div>
        ) : null}

        {status === 'confirm' && plan ? <PaidPlan plan={plan} choice={choice} setChoice={setChoice} /> : null}

        {status === 'loading' ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="wb-ai-loading">
            <Loader2 className="h-4 w-4 animate-spin" />Generating{choice?.illustration ? ' (illustrations can take up to a minute)' : ''}…
          </p>
        ) : null}

        {status === 'preview' ? (
          <div className="space-y-2" data-testid="wb-ai-preview">
            <AiPreviewCanvas objects={previewObjects} background={background} />
            {diagram ? (
              <p className="text-xs text-muted-foreground" data-testid="wb-ai-preview-summary">
                {LAYOUTS[diagram.layout] || 'Freeform AI layout'} · {diagram.objects.length} editable objects{diagram.dropped ? ` (${diagram.dropped} invalid items were skipped)` : ''}.
                {hasObjects ? ' Replacing will remove the objects currently on this whiteboard.' : ''}
              </p>
            ) : null}
            <IllustrationInfo ill={ill} variant={variant} setVariant={setVariant} />
            <ErrorBox testid="wb-ai-diagram-error" error={errors.diagram ? `Diagram: ${errors.diagram}` : ''} />
            <ErrorBox testid="wb-ai-ill-error" error={errors.illustration ? `Illustration: ${errors.illustration}` : ''} />
            {diagram?.warnings?.filter((w) => !w.startsWith('Illustration requested')).length > 0 && (
              <ul data-testid="wb-ai-warnings" className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 space-y-0.5">
                {diagram.warnings.filter((w) => !w.startsWith('Illustration requested')).map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </div>
        ) : null}

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" className="rounded-sm" onClick={close} disabled={busy} data-testid="wb-ai-cancel">Cancel</Button>
          {status === 'confirm' ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => setStatus('idle')} data-testid="wb-ai-edit-prompt">Edit prompt</Button>
              <Button className="rounded-sm" onClick={() => run(choice)} disabled={!nPaid || (choice.illustration && choice.subject.trim().length < 3)} data-testid="wb-ai-confirm-run">
                Confirm and generate ({nPaid} paid call{nPaid === 1 ? '' : 's'})
              </Button>
            </>
          ) : null}
          {status === 'preview' ? (
            <>
              <Button variant="outline" className="rounded-sm" onClick={() => { resetResults(); setStatus('idle'); }} data-testid="wb-ai-edit-prompt">Edit prompt</Button>
              {ill ? (
                <Button variant={diagram?.objects?.length ? 'outline' : 'default'} className="rounded-sm" onClick={addIllustration} data-testid="wb-ai-add-illustration">
                  {diagram?.objects?.length ? 'Add illustration only' : 'Add to whiteboard'}
                </Button>
              ) : null}
              {diagram?.objects?.length ? (
                <Button className="rounded-sm" onClick={replaceAll} data-testid="wb-ai-replace">{ill ? 'Replace whiteboard with both' : 'Replace Whiteboard'}</Button>
              ) : null}
            </>
          ) : null}
          {['idle', 'planning', 'error'].includes(status) ? (
            <Button className="rounded-sm" onClick={start} disabled={busy || prompt.trim().length < 3 || prompt.length > PROMPT_LIMIT} data-testid="wb-ai-generate">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Icon className="h-4 w-4 mr-2" />}
              {busy ? 'Checking…' : mode === 'illustration' ? 'Continue' : 'Generate (uses AI credits)'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
